import { useRef, useState } from 'react';
import type { WorklogListItem } from '../../types/worklog.types';

/**
 * 현장목록 우측 패널 공통 선택 규칙(REQ-C-01, 설계서 §2.1.1) — 토글 단일선택.
 *
 * - 화면 최초 진입(첫 조회 완료) 시 첫 현장을 자동 선택.
 * - 카드 클릭 = 토글: 다른 현장을 누르면 그 현장으로 교체, 같은 현장을 다시 누르면 해제.
 * - 전체 해제 상태(selectedSiteId=null)에서는 빈 화면 안내를 보여줌(화면별 문구는 페이지가 담당).
 *
 * SCR-WO-LIST·SCR-FR-CONFIRM·SCR-STATUS·SCR-CS-EDIT 4개 화면이 공용으로 쓴다 — 기존에는 화면마다
 * `deselectedSites: Set<number>`(기본 전체선택 + 클릭으로 해제하는 다중선택) 상태를 각자 들고 있었으나,
 * 그 모델은 REQ-C-01의 "토글 단일선택" 규칙과 달라 이 훅으로 통일한다.
 */
export function useSiteSelection() {
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const firstPickDone = useRef(false);

  const isSiteSelected = (siteId: number) => selectedSiteId === siteId;

  const toggleSite = (siteId: number) =>
    setSelectedSiteId((prev) => (prev === siteId ? null : siteId));

  /** 현장 등록/확인 완료 시 그 현장 하나로 교체 — SiteArea의 onSiteRegistered에 그대로 연결. */
  const selectOnlySite = (siteId: number) => setSelectedSiteId(siteId);

  /** SiteArea의 onSitesLoaded에 그대로 연결 — 최초 1회만 첫 현장을 자동 선택한다. */
  const onSitesLoaded = (siteIds: number[], _items: WorklogListItem[]) => {
    if (firstPickDone.current) return;
    firstPickDone.current = true;
    setSelectedSiteId(siteIds[0] ?? null);
  };

  return { selectedSiteId, isSiteSelected, toggleSite, selectOnlySite, onSitesLoaded };
}
